import React, { type FunctionComponent, useMemo } from 'react'
import { useDomainMessages } from '../../../utils/domain/intl.ts'
import {
  Input,
  PropertyContainer,
  PropertyRow,
  PropertyTitle,
} from '../../shared-styled-components/SharedStyledComponents'
import {
  AttributeManagementNextStepTypeEnum,
  type PipelineStep,
  type PipelineStepNextStepsRule,
} from '../../../types/attributeManagement.types.ts'

interface Props {
  step: PipelineStep
  onChange: (step: PipelineStep) => void
}

const MAX_NUM = 10
const MIN_NUM = 1

export const PipelineFormStepControlFieldMatch: FunctionComponent<Props> = ({ step, onChange }) => {
  const i18n = useDomainMessages({
    field:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlFieldMatch.field',
    numberOfFields:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlFieldMatch.numberOfFields',
    countsRequired:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.pipelineForm.controlFieldMatch.countsRequired',
  })

  const nextStepsRule: PipelineStepNextStepsRule = useMemo(
    () => ({
      type: AttributeManagementNextStepTypeEnum.FieldMatch,
      countRequired: step?.nextStepsRule?.countRequired ?? 0,
      campFields: step?.nextStepsRule?.campFields ?? [],
      nextPipelineStepOnMatchHit: step?.nextStepsRule?.nextPipelineStepOnMatchHit ?? '',
      nextPipelineStepOnMatchMiss: step?.nextStepsRule?.nextPipelineStepOnMatchMiss ?? '',
      possibleNextSteps: step?.nextStepsRule?.possibleNextSteps ?? [],
    }),
    [
      step?.nextStepsRule?.campFields,
      step?.nextStepsRule?.countRequired,
      step?.nextStepsRule?.nextPipelineStepOnMatchHit,
      step?.nextStepsRule?.nextPipelineStepOnMatchMiss,
      step?.nextStepsRule?.possibleNextSteps,
    ]
  )

  return (
    <>
      <PropertyContainer style={{ flexDirection: 'column' }}>
        <PropertyRow>
          <PropertyTitle>{i18n.numberOfFields}</PropertyTitle>
          <Input
            type={'number'}
            value={nextStepsRule.possibleNextSteps.length}
            onChange={e => {
              const amount = Math.min(MAX_NUM, Math.max(MIN_NUM, Number.parseInt(e.target.value)))

              const fields: string[] = []
              for (let i = 0; i < amount; i++) {
                fields.push(nextStepsRule?.campFields?.[i] ?? '')
              }

              const existingPossibleNextSteps = step?.nextStepsRule?.possibleNextSteps ?? []
              const possibleNextSteps = Array(amount)
                .fill('')
                .map((_, index) => existingPossibleNextSteps[index] ?? '')

              onChange({
                ...step,
                nextStepsRule: {
                  ...nextStepsRule,
                  campFields: fields,
                  possibleNextSteps: possibleNextSteps,
                },
              })
            }}
          />
        </PropertyRow>
        {(nextStepsRule.campFields ?? []).map((field, index) => (
          <PropertyRow key={`${index}`}>
            <PropertyTitle>
              {i18n.field} {index + 1}:
            </PropertyTitle>
            <Input
              type={'text'}
              value={field}
              onChange={e => {
                if (!nextStepsRule.campFields) return
                nextStepsRule.campFields[index] = e.target.value
                onChange({
                  ...step,
                  nextStepsRule: nextStepsRule,
                })
              }}
            />
          </PropertyRow>
        ))}
        <PropertyRow>
          <PropertyTitle>{i18n.countsRequired}</PropertyTitle>
          <Input
            type={'number'}
            value={nextStepsRule.countRequired}
            onChange={e => {
              onChange({
                ...step,
                nextStepsRule: {
                  ...nextStepsRule,
                  countRequired: Math.max(
                    0,
                    Math.min(
                      (nextStepsRule.campFields ?? []).length,
                      Number.parseInt(e.target.value)
                    )
                  ),
                },
              })
            }}
          />
        </PropertyRow>
      </PropertyContainer>
    </>
  )
}
